/**
 * Authentication Context
 * Manages user authentication state and provides auth functions
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi, type User, type LoginCredentials } from '@/api/auth'
import { registerSessionExpiredHandler } from '@/lib/sessionEvents'
import { toast } from '@/lib/toast'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  endSession: (reason: 'idle' | 'expired') => void
  refreshUser: () => Promise<void>
  hasRole: (role: string) => boolean
  hasAnyRole: (...roles: string[]) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Load user from token on mount
   */
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('access_token')

      if (token) {
        try {
          const currentUser = await authApi.getCurrentUser()
          setUser(currentUser)
        } catch (error) {
          console.error('Failed to load user:', error)
          // Clear invalid tokens
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      }

      setIsLoading(false)
    }

    loadUser()
  }, [])

  /**
   * Login with credentials
   */
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true)
    try {
      // Get tokens
      const tokenData = await authApi.login(credentials)

      // Store tokens
      localStorage.setItem('access_token', tokenData.access_token)
      localStorage.setItem('refresh_token', tokenData.refresh_token)

      // Get user info
      const currentUser = await authApi.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    // Call logout endpoint (best effort)
    authApi.logout().catch((error) => {
      console.error('Logout API call failed:', error)
    })

    // Clear local state
    setUser(null)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }, [])

  /**
   * End the session without the user explicitly choosing to log out -
   * either due to inactivity, or because the refresh token died server-side.
   * Unlike logout(), this never hard-navigates: clearing `user` flips
   * `isAuthenticated` to false and ProtectedRoute handles the redirect.
   */
  const endSession = useCallback((reason: 'idle' | 'expired') => {
    if (reason === 'idle') {
      // Best effort - user is still technically authenticated server-side
      authApi.logout().catch(() => {})
      toast.info('Logged out due to inactivity', 'Please log in again to continue.')
    } else {
      toast.warning('Session expired', 'Please log in again.')
    }

    setUser(null)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }, [])

  /**
   * Let the axios interceptor (outside the React tree) end the session
   * when a token refresh fails.
   */
  useEffect(() => {
    return registerSessionExpiredHandler(() => endSession('expired'))
  }, [endSession])

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authApi.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Failed to refresh user:', error)
      throw error
    }
  }, [])

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.roles.includes(role) || false
    },
    [user]
  )

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (...roles: string[]): boolean => {
      return roles.some((role) => user?.roles.includes(role)) || false
    },
    [user]
  )

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    endSession,
    refreshUser,
    hasRole,
    hasAnyRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
